import { faArrowRight } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { motion } from "framer-motion";

import { EcosystemId, EcosystemLogo } from "~/components/logos/ecosystem";
import { CTAPrimary } from "~/components/site/call-to-action-primary";
import { container, item, Layout } from "~/components/site/layout";
import { ROUTE_DATA } from "~/lib/constants";
import { getMeta } from "~/lib/get-meta";
import { getUrls } from "~/lib/get-urls";

export type Step = Readonly<{
  image: string;
  text: string;
}>;

type CaseStudy = Readonly<{
  id: string;
  name: string;
  persona: string;
  excerpt: string;
  borderColor: string;
  href: string;
}>;

const caseStudies: CaseStudy[] = [
  {
    id: "glo-dollar",
    name: "Glo Foundation",
    persona: "Community Currency",
    excerpt:
      "Glo Foundation's mission to unlock new sources of funding for public goods and charity through zero-cost philanthropy needed a partner to help bring a new stablecoin to market.",
    borderColor: "hover:border-[#2AE2D9]",
    href: "glo-dollar",
  },
  {
    id: "etherfuse",
    name: "Etherfuse",
    persona: "DeFi Protocol",
    excerpt:
      "Etherfuse was looking for a regulated partner to issue a Mexican Peso stablecoin.",
    borderColor: "hover:border-[#d9ff28]",
    href: "etherfuse",
  },
  {
    id: "ethereum-classic",
    name: "Ethereum Classic",
    persona: "Emerging Ecosystem",
    excerpt:
      "Ethereum Classic needed to bring the first native stablecoin to the network to support its growing DeFi ecosystem, requiring a regulated issuance partner who could implement a custom stablecoin.",
    borderColor: "hover:border-[#35ff86]",
    href: "ethereum-classic",
  },
];

const { desc, path, title } = ROUTE_DATA.caseStudies;

export function loader({ request }: LoaderFunctionArgs) {
  return {
    urls: getUrls({ path, request }),
  };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return data ? getMeta({ desc, title, urls: data.urls }) : [];
};

export default function CaseStudies(): JSX.Element {
  return (
    <Layout>
      <section className="section-first">
        <div className="container">
          <motion.div variants={item} className="max-w-3xl">
            <h1 className="eyebrow">Case studies</h1>
            <h2 className="mt-0 mb-16">Hear from our customers</h2>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {caseStudies.map((c) => (
              <motion.a
                variants={item}
                key={c.persona}
                href={`/case-studies/${c.href}`}
                className={`${c.borderColor} global-hover-transition round p-6 bg-secondary no-underline border border-primary flex flex-col gap-y-6 relative group`}
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full border border-primary px-3 py-1 text-sm font-medium text-primary bg-secondary whitespace-nowrap">
                    {c.persona}
                  </span>
                  <span className="p-2 border border-primary rounded-full opacity-0 relative -right-[10px] group-hover:opacity-100 group-hover:translate-x-[-10px] transition-all duration-500 leading-none">
                    <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
                  </span>
                </div>
                <div className="flex justify-start">
                  <h3 className="hidden">{c.name}</h3>
                  <EcosystemLogo ecosystemId={c.id as EcosystemId} />
                </div>
                <p className="m-0 !text-base font-normal text-secondary">
                  {c.excerpt}
                </p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>
      <motion.div
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <motion.div variants={item}>
          <CTAPrimary
            heading="Create a stablecoin with Brale"
            subheading="Collaborate, plan, and build with us."
            primaryBtn="Talk to sales"
            primaryBtnUrl="/contact"
            secondaryBtn="See pricing"
            secondaryBtnUrl="/pricing"
          />
        </motion.div>
      </motion.div>
    </Layout>
  );
}
